import React, { useState } from 'react';

import { Button, Pagination, showToast, Tooltip } from '@attentive-platform/stem-ui';

import {
  ActionCluster,
  ActionsCol,
  CredentialsTableEl,
  MaskedValue,
  PasswordCol,
  PaginationRow,
  PasswordRow,
  PlanroomCol,
  TableSection,
  UrlCol,
  UsernameCol
} from './styles';

const Truncated = ({ value, className }) => {
  if (!value) return <span className='text-gray-400'>—</span>;
  return (
    <Tooltip arrow placement='top' variant='dark' title={value}>
      <span className={`block truncate ${className || ''}`}>{value}</span>
    </Tooltip>
  );
};

const CredentialRow = ({ planroom, rowId, shown, removing, onTogglePassword, onCopyPassword, onEdit, onRemove }) => {
  const handleToggle = () => onTogglePassword(rowId);
  const handleCopy = () => onCopyPassword(planroom.password);
  const handleEdit = () => onEdit(planroom);
  const handleRemove = () => onRemove(planroom);

  return (
    <tr className='border-t border-gray-200'>
      <td className='px-4 py-3 font-semibold text-gray-900'>
        <Truncated value={planroom.name} />
      </td>
      <td className='px-4 py-3'>
        {planroom.url ? (
          <a href={planroom.url} target='_blank' rel='noreferrer' className='no-underline'>
            <Truncated value={planroom.url} className='text-blue-600' />
          </a>
        ) : (
          <span className='text-gray-400'>—</span>
        )}
      </td>
      <td className='px-4 py-3 text-gray-800'>
        <Truncated value={planroom.username} />
      </td>
      <td className='px-4 py-3 text-gray-800'>
        <PasswordRow>
          <MaskedValue masked={!shown}>
            {planroom.password ? (shown ? planroom.password : '••••••••••') : '—'}
          </MaskedValue>
          <ActionCluster>
            <Button size='small' variant='text' color='primary' disabled={!planroom.password} onClick={handleToggle}>
              {shown ? 'Hide' : 'Show'}
            </Button>
            <Button size='small' variant='text' color='primary' disabled={!planroom.password} onClick={handleCopy}>
              Copy
            </Button>
          </ActionCluster>
        </PasswordRow>
      </td>
      <td className='px-4 py-3'>
        <ActionCluster>
          <Button size='small' variant='text' color='primary' onClick={handleEdit}>
            Edit
          </Button>
          <Button
            size='small'
            variant='text'
            color='error'
            loading={removing}
            disabled={!planroom.id || removing}
            onClick={handleRemove}>
            Remove
          </Button>
        </ActionCluster>
      </td>
    </tr>
  );
};

const CredentialsTable = ({
  companyName,
  planrooms,
  loading,
  removingId,
  page,
  pageCount,
  onPageChange,
  onAdd,
  onEdit,
  onRemove
}) => {
  const [visibleIds, setVisibleIds] = useState({});

  const togglePassword = id => {
    setVisibleIds(current => ({ ...current, [id]: !current[id] }));
  };

  const copyPassword = async password => {
    if (!password) {
      showToast('No password to copy', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(password);
      showToast('Password copied', 'success');
    } catch {
      showToast('Could not copy password', 'error');
    }
  };

  return (
    <TableSection>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <h3 className='m-0 text-base font-semibold text-gray-900'>{companyName}</h3>
        <Button size='medium' variant='outlined' color='primary' onClick={onAdd}>
          + Add credential
        </Button>
      </div>
      <div className='overflow-hidden rounded-lg border border-gray-200'>
        <CredentialsTableEl>
          <colgroup>
            <PlanroomCol />
            <UrlCol />
            <UsernameCol />
            <PasswordCol />
            <ActionsCol />
          </colgroup>
          <thead>
            <tr className='bg-gray-100 text-gray-900'>
              <th className='px-4 py-3 font-semibold'>PlanRoom</th>
              <th className='px-4 py-3 font-semibold'>Login URL</th>
              <th className='px-4 py-3 font-semibold'>Username</th>
              <th className='px-4 py-3 font-semibold'>Password</th>
              <th className='px-4 py-3 font-semibold'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className='px-4 py-6 text-gray-500' colSpan={5}>
                  Loading planrooms…
                </td>
              </tr>
            )}
            {!loading && planrooms.length === 0 && (
              <tr>
                <td className='px-4 py-6 text-gray-500' colSpan={5}>
                  No planrooms found
                </td>
              </tr>
            )}
            {!loading &&
              planrooms.map((planroom, index) => {
                const rowId = planroom.id || `${planroom.name}-${index}`;
                return (
                  <CredentialRow
                    key={rowId}
                    planroom={planroom}
                    rowId={rowId}
                    shown={Boolean(visibleIds[rowId])}
                    removing={removingId === planroom.id}
                    onTogglePassword={togglePassword}
                    onCopyPassword={copyPassword}
                    onEdit={onEdit}
                    onRemove={onRemove}
                  />
                );
              })}
          </tbody>
        </CredentialsTableEl>
      </div>
      {pageCount > 1 && (
        <PaginationRow>
          <Pagination
            size='small'
            color='primary'
            count={pageCount}
            page={page}
            disabled={loading}
            onChange={onPageChange}
          />
        </PaginationRow>
      )}
    </TableSection>
  );
};

export default CredentialsTable;
